import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  addAddressAction,
  reloadUserAddresses,
  updateAddressAction,
  updateAddressTypeAction,
  deleteAddressAction,
  updateReturnCarrierAction,
} from "@actions/address-actions";
import carriers from "@utils/constants/carriers";

export function useUserAddress() {
  return useSelector((state) => state.address);
}

export function useUserAddresses() {
  return useSelector((state) => state.address.addresses);
}

export function useUserShippingAddress() {
  const addresses = useSelector((state) => state.address.addresses);

  return addresses.find((address) => address.shipping);
}

export function useUserBillingAddress() {
  const addresses = useSelector((state) => state.address.addresses);

  return addresses.find((address) => address.billing);
}

export function useUserCollectionAddress() {
  const addresses = useSelector((state) => state.address.addresses);

  return addresses.find((address) => address.collection);
}

export function useUserDropOffAddress() {
  const addresses = useSelector((state) => state.address.addresses);

  return addresses.find((address) => address.drop_off);
}

export function useHasMultipleCollectionMethods() {
  const shippingAddress = useUserShippingAddress();
  const carriersSpecs = useSelector((state) => state.features.carriers_specificities);

  return (
    carriersSpecs.find((country) => country.iso_3166 === shippingAddress?.country)
      ?.return_pickup_carriers.length > 1
  );
}

export function useLocalPartnerNameFor(country) {
  const carriersSpecs = useSelector((state) => state.features.carriers_specificities);
  const countrySpecs = carriersSpecs.find((c) => c.iso_3166 === country);

  return (carrierName) => {
    if (carrierName === carriers.colissimo && countrySpecs?.colissimo_local_partner_name) {
      return countrySpecs.colissimo_local_partner_name;
    }

    if (carrierName === carriers.chronopost && countrySpecs?.chronopost_local_partner_name) {
      return countrySpecs.chronopost_local_partner_name;
    }

    return carrierName;
  };
}

export function useDefaultReturnHomeCarrierFor(country) {
  const countrySpecs = useSelector((state) => state.features.carriers_specificities).find(
    (c) => c.iso_3166 === country,
  );

  return countrySpecs?.default_return_home_carrier;
}

export function useBalReturnAvailableFor(country) {
  const countrySpecs = useSelector((state) => state.features.carriers_specificities).find(
    (c) => c.iso_3166 === country,
  );

  return countrySpecs?.bal_return_available;
}

export function useReturnPickupCarriersFor(country) {
  const countrySpecs = useSelector((state) => state.features.carriers_specificities).find(
    (c) => c.iso_3166 === country,
  );

  return countrySpecs?.return_pickup_carriers;
}

export function useDeliveryPickupCarriersFor(country) {
  const countrySpecs = useSelector((state) => state.features.carriers_specificities).find(
    (c) => c.iso_3166 === country,
  );

  return countrySpecs?.delivery_pickup_carriers;
}

export function useIsBalDeliveryAvailableForCountry(country) {
  const countrySpecs = useSelector((state) => state.features.carriers_specificities).find(
    (c) => c.iso_3166 === country,
  );

  return countrySpecs?.direct_bal_delivery;
}

export function useReloadUserAddresses() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(reloadUserAddresses());
  }, [dispatch]);
}

export function useAddAddress() {
  const dispatch = useDispatch();

  return async (address, addressType) => dispatch(addAddressAction(address, addressType));
}

export function useUpdateAddress() {
  const dispatch = useDispatch();

  return async (id, address) => dispatch(updateAddressAction(id, address));
}

export function useDeleteAddress() {
  const dispatch = useDispatch();

  return async (addressId) => dispatch(deleteAddressAction(addressId));
}

export function useUpdateAddressType() {
  const dispatch = useDispatch();

  return async (id, data, addressType) => dispatch(updateAddressTypeAction(id, data, addressType));
}

export function useUpdateReturnCarrier() {
  const dispatch = useDispatch();

  return async (carrierName) => dispatch(updateReturnCarrierAction(carrierName));
}

export function useIsLoadingAddresses() {
  return useSelector((state) => state.address.areAddressesLoading);
}
